
import {
  defineComponent,
  useRoute,
  computed,
  watch,
  useMeta,
  useFetch,
  useRouter,
  reactive,
  ref,
  useContext,
} from '@nuxtjs/composition-api';

import {
  usePagePreloader,
  useFetchCategory,
  useBreadcrumbs,
} from '~/composables';
import { getProductContent } from '~/helpers/vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import CatalogLayout from '@/domains/catalog/components/CatalogLayout.vue';
import CatalogHead from '@/domains/catalog/components/CatalogHead.vue';
import CatalogProductCard from '@/domains/catalog/components/CatalogProductCard.vue';
import CatalogCategoriesNav from '@/domains/catalog/components/CatalogCategoriesNav.vue';
import CatalogFilter from '@/domains/catalog/components/CatalogFilter.vue';

export default defineComponent({
  components: {
    AppBreadcrumbs,
    CatalogLayout,
    CatalogHead,
    CatalogCategoriesNav,
    CatalogProductCard,
    CatalogFilter,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { error, ssrContext } = useContext();
    const { start: startPageLoading, stop: stopPageLoading } =
      usePagePreloader();
    const {
      loading: catalogIsLoading,
      start: startCatalogLoading,
      stop: stopCatalogLoading,
    } = usePagePreloader('catalog');
    const currentPage = computed(() =>
      route.value.query.page ? +route.value.query.page : undefined,
    );
    const pageSlug = computed(() => route.value.params.slug);
    const { breadcrumbs, setCrumbs } = useBreadcrumbs();
    const {
      fetchCategory,
      getPageAggs,
      category,
      categoryAggs,
      categoryHeader,
      categoryContent,
      categoryProducts,
      catalogNavItems,
      pagesCount,
    } = useFetchCategory();

    const aggs = reactive(getPageAggs(route.value.query));
    const sortingHasChanged = ref(false);

    const doFetchCategory = async () => {
      startCatalogLoading(!sortingHasChanged.value);
      sortingHasChanged.value = false;
      try {
        await fetchCategory(route.value.params.slug, {
          page: currentPage.value,
          limit: aggs.limit,
          ...aggs.filters,
          ...aggs.sorting,
        });

        const categoriesTree = category.value.branch.length
          ? category.value.branch.reduceRight((tree, currentBranch, index) => {
              return [
                ...tree,
                {
                  text: currentBranch.name,
                  link:
                    index > 0 ? `/catalog/${currentBranch.slug}` : undefined,
                },
              ];
            }, [])
          : [
              {
                text: category.value?.name,
              },
            ];

        setCrumbs('main', 'catalog', ...categoriesTree);
      } catch (_) {
        if (ssrContext) ssrContext.res.statusCode = 404;
        else error({ statusCode: 404 });
      } finally {
        stopCatalogLoading();
      }
    };

    const updateRouterQuery = () => {
      router.push({
        query: { ...aggs.filters, ...aggs.sorting, page: currentPage.value },
      });
    };

    const updatePage = async () => {
      updateRouterQuery();
      await doFetchCategory();
    };

    const changeSorting = (value) => {
      aggs.sorting = value;
      sortingHasChanged.value = true;
    };

    watch(aggs, () => {
      updatePage();
    });

    watch(currentPage, () => {
      updatePage();
    });

    useFetch(async () => {
      startPageLoading();
      await doFetchCategory();
      stopPageLoading();
    });

    useMeta(() => ({
      title: category.value?.seo.page_title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: category.value?.seo.description,
        },
        {
          property: 'og:title',
          content: category.value?.seo.og_title,
        },
      ],
    }));

    return {
      pageSlug,
      catalogIsLoading,
      breadcrumbs,
      aggs,
      category,
      categoryAggs,
      categoryHeader,
      categoryContent,
      categoryProducts,
      catalogNavItems,
      pagesCount,
      getProductContent,
      changeSorting,
      currentPage,
    };
  },
  head: {},
});
